import { tr } from "./i18n.js";
/**
 * Article top bar — one button per social post, reposts included.
 *
 * The backend sends `article.social_post_history` (built from the
 * append-only publish log by backend/social_history.py): every post,
 * oldest first, each tagged with `kind` ("feed" | "story") and
 * `repost_number` (0 = the original). Older payloads (or a failed log
 * read) only carry `social_posts`, so that is the fallback.
 *
 * Pure — no React — so it is unit-tested with node:
 *     node --test frontend/scripts/socialHistory.test.mjs
 */

/** Reposts shown per group before a "+N more" toggle. */
export const VISIBLE_REPOSTS = 2;

function kindOf(p) {
  return String((p && p.variant) || "").toLowerCase() === "story" ? "story" : "feed";
}

/**
 * Group posts by (platform, page, kind), keeping the order the server
 * sent (groups by their original's time, reposts oldest → newest).
 * Returns [{ key, original, reposts: [] }].
 */
export function historyGroups(article) {
  const hist = (article && article.social_post_history) || [];
  const rows = hist.length
    ? hist
    : ((article && article.social_posts) || []).filter(Boolean);
  const groups = [];
  const index = new Map();
  for (const r of rows) {
    if (!r || !r.platform) continue;
    const kind = r.kind || kindOf(r);
    const key = `${r.platform}|${r.page_id || ""}|${kind}`;
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ key, kind, original: null, reposts: [] });
    }
    const g = groups[index.get(key)];
    if (!g.original) g.original = { ...r, kind };
    else g.reposts.push({ ...r, kind });
  }
  return groups;
}

/** Which reposts to render: the newest VISIBLE_REPOSTS unless expanded. */
export function visibleReposts(group, expanded) {
  const all = (group && group.reposts) || [];
  if (expanded || all.length <= VISIBLE_REPOSTS) return { shown: all, hidden: 0 };
  return {
    shown: all.slice(all.length - VISIBLE_REPOSTS),
    hidden: all.length - VISIBLE_REPOSTS,
  };
}

/** Page / account name for a post. */
export function accountName(p) {
  if (!p) return "";
  return p.platform === "facebook"
    ? p.page_name || "Facebook"
    : p.ig_username || p.page_name || "Instagram";
}

/**
 * Chip text. Original: "GG North" (+ " · Story"). Repost: "Repost · Sep 28"
 * — `fmtDate(iso)` formats the date (property timezone); without a date
 * it falls back to "Repost 2".
 */
export function chipLabel(p, fmtDate) {
  const story = p && p.kind === "story" ? tr(" · Story") : "";
  if (!p || !p.repost_number) return accountName(p) + story;
  const when = p.posted_at && fmtDate ? fmtDate(p.posted_at) : "";
  return (when ? tr("Repost · {when}", { when }) : tr("Repost {n}", { n: p.repost_number })) + story;
}

/** Tooltip, e.g. "Facebook repost 2 on GG North". */
export function chipTitle(p) {
  const platform = p && p.platform === "facebook" ? "Facebook" : "Instagram";
  const story = p && p.kind === "story";
  const what = story ? tr("Story") : tr("post");
  const n = p && p.repost_number;
  const lead = n
    ? `${platform} ${story ? tr("Story repost") : tr("repost")} ${n}`
    : `${platform} ${what}`;
  return tr("{lead} on {account}", { lead, account: accountName(p) });
}
